// Helper subcomponents for FirearmSale (kept here so the main file stays
// under the file-size cap).

import { type ReactNode, useState } from "react";
import { RuleRow } from "./components";
import { Check, TouchButton } from "./MockShell";
import { showToast } from "./toast";

// iOS Settings/Contacts-style row: small label on top, value below, wraps
// freely so long emails and addresses never get clipped.
export function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="px-4 py-3 hairline last:shadow-none">
      <div className="text-[12px] text-slate-500 uppercase tracking-wider font-medium">{label}</div>
      <div className="mt-0.5 text-[15px] text-slate-900 break-words">{value}</div>
    </div>
  );
}

export function PrimaryContinue({
  onNext,
  label = "Continue",
}: {
  onNext: () => void;
  label?: string;
}) {
  return (
    <div className="mt-6">
      <TouchButton size="lg" full onClick={onNext}>
        {label}
      </TouchButton>
    </div>
  );
}

export function ResetLink({ onReset }: { onReset: () => void }) {
  return (
    <div className="mt-3 text-center">
      <TouchButton variant="ghost" onClick={onReset}>
        Reset outcome
      </TouchButton>
    </div>
  );
}

export function OutcomeIcon({ tone }: { tone: "ok" | "warn" | "danger" }) {
  const styles = {
    ok: "bg-emerald-100 text-emerald-600",
    warn: "bg-amber-100 text-amber-600",
    danger: "bg-rose-100 text-rose-600",
  }[tone];
  const glyph = tone === "ok" ? "✓" : "!";
  return (
    <span
      className={`h-8 w-8 rounded-full ${styles} grid place-items-center text-[15px] font-bold shrink-0`}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
}

type ReleaseStage = "idle" | "confirming" | "committed";

export function ReleaseStep({
  ticketId,
  serial,
  buyerName,
  lockIncluded,
}: {
  ticketId: string;
  serial: string;
  buyerName: string;
  lockIncluded?: boolean;
}) {
  const [stage, setStage] = useState<ReleaseStage>("idle");

  return (
    <>
      <p className="text-[14px] text-slate-500 px-1 mb-4">
        Final checks before giving the firearm to the buyer.
      </p>

      <div className="bg-white rounded-2xl overflow-hidden">
        {lockIncluded ? (
          <RuleRow ok label="Gun lock" detail="Ships in the factory case" />
        ) : (
          <RuleRow
            warn
            label="Gun lock"
            detail="Add a lock to the sale or record an exception"
            action="Add lock"
          />
        )}
        <RuleRow info label="ATF notice sheet" detail="Prints with the receipt" />
        <RuleRow warn label="3310.4" detail="Due by close of business today" />
      </div>

      <div className="mt-5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-2">
        Last step
      </div>

      {stage === "idle" && (
        <div className="bg-emerald-50 rounded-2xl p-5">
          <div className="text-[15px] text-emerald-900">
            After the buyer physically takes the firearm from the counter, tap below. This closes
            the ticket and writes the disposition to the bound book.
          </div>
          <div className="mt-4">
            <TouchButton size="lg" full onClick={() => setStage("confirming")}>
              Firearm given to buyer
            </TouchButton>
          </div>
        </div>
      )}

      {stage === "confirming" && (
        <div className="bg-amber-50 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <span className="h-8 w-8 rounded-full bg-amber-500 text-white grid place-items-center text-[15px] font-bold shrink-0">
              !
            </span>
            <div className="min-w-0">
              <div className="text-[17px] font-semibold text-amber-900 tracking-tight">
                Confirm the handover
              </div>
              <div className="text-[14px] text-amber-900/80 mt-1 break-words">
                Tapping confirm will close ticket {ticketId} and write the disposition for serial{" "}
                {serial} to the bound book. This cannot be undone from here.
              </div>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <TouchButton size="lg" variant="secondary" onClick={() => setStage("idle")}>
              Cancel
            </TouchButton>
            <TouchButton
              size="lg"
              onClick={() => {
                setStage("committed");
                showToast("Disposition written · bound book updated");
              }}
            >
              Confirm & commit
            </TouchButton>
          </div>
        </div>
      )}

      {stage === "committed" && (
        <div className="bg-emerald-50 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <span className="h-8 w-8 rounded-full bg-emerald-500 text-white grid place-items-center">
              <Check />
            </span>
            <div className="min-w-0">
              <div className="text-[17px] font-semibold text-emerald-900 tracking-tight">
                Done — bound book updated
              </div>
              <div className="text-[14px] text-emerald-900/80 mt-1 break-words">
                Ticket {ticketId} is closed. Serial {serial} recorded as disposed to {buyerName}{" "}
                today.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4">
        <TouchButton
          size="lg"
          full
          variant="secondary"
          onClick={() => {
            window.location.hash = "#/mocks/today";
          }}
        >
          Done
        </TouchButton>
      </div>
    </>
  );
}

export function OutcomeCard({
  tone,
  label,
  children,
}: {
  tone: "ok" | "warn" | "danger";
  label: string;
  children: ReactNode;
}) {
  const bg = tone === "ok" ? "bg-emerald-50" : tone === "warn" ? "bg-amber-50" : "bg-rose-50";
  const textColor =
    tone === "ok" ? "text-emerald-900" : tone === "warn" ? "text-amber-900" : "text-rose-900";
  const subColor =
    tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-rose-700";
  return (
    <div className={`rounded-2xl ${bg} p-6`}>
      <div className={`text-[13px] uppercase tracking-wider font-medium ${subColor}`}>Outcome</div>
      <div className={`text-[32px] font-semibold ${textColor} tracking-tight`}>{label}</div>
      {children}
    </div>
  );
}
